import {
  OpsanioServiceModel,
  OpsanioSizeValues,
  OpsanioSizing,
} from "src/questionnaires/functions";
import { OpsanioAptRing } from "./apt-ring";
import { OpsanioHardening } from "./hardening";

export * from "./apt-ring";
export * from "./hardening";

export interface OpsanioMetadata {
  name: string;
  project: string;
  owner: string;
  stage: string;
}

export interface OpsanioGeneralContacts {
  sales: Array<string>;
  technical: Array<string>;
  customer: Array<string>;
  /**
   * Dedicated recipient list for wave update notifications, taken from the
   * "Update notification recipients" field. Plain e-mail addresses only; these
   * are never provisioned as Keycloak users.
   */
  updateNotifications: Array<string>;
}
export interface OpsanioGeneral {
  contacts: OpsanioGeneralContacts;
  dates: {
    start: string;
    end: string;
  };
  poc: OpsanioPoc;
}

/**
 * Contact selections captured in the "Create questionnaire" wizard and seeded
 * into the General section's contact fields at creation time. The opsanio
 * (internal) contacts are Keycloak user ids picked from the sales/technical
 * contact groups; the customer contacts are plain e-mail addresses (optional -
 * they may be left empty and filled in later). Values are joined with "," and
 * written verbatim onto the matching ComplexQuestion facts:
 *   sales               -> general.contacts.sales_contact
 *   technical           -> general.contacts.technical_contact
 *   customerSales       -> general.customers.customer_sales_contact
 *   customerTechnical   -> general.customers.customer_technical_contact
 *   customerOrg.        -> general.customers.customer_organizational_contact
 */
export interface OpsanioCreateContactDefaults {
  /** Keycloak user ids from the opsanio sales contact group. */
  sales: string[];
  /** Keycloak user ids from the opsanio technical contact group. */
  technical: string[];
  /** Customer sales contact e-mail addresses (may be empty). */
  customerSales: string[];
  /** Customer technical contact e-mail addresses (may be empty). */
  customerTechnical: string[];
  /** Customer organizational contact e-mail addresses (may be empty). */
  customerOrganizational: string[];
}

export interface OpsanioLocationValue {
  /** Latitude/longitude as resolved by the geocoder. Both are absent for a
   *  manually entered address when no coordinates were supplied - use
   *  `hasCoordinates()` (src/helper/map) before pinning or linking a map. */
  lat?: number;
  lng?: number;
  displayName: string;
  city?: string;
  country?: string;
  postalCode?: string;
  street?: string;
  /** Free-text comment for this site (Kommentar). */
  comment?: string;
  /** Further site information (Weitere Standortinformationen). */
  weitereInfos?: string;
  /** Network breakouts at this site (Breakouts in Netzwerk). */
  breakouts?: string;
  /** Marks the primary/main site. Exactly one entry should be primary; the
   *  fact parser uses it to pick the single location handed to downstream
   *  consumers (helm values, mail, netbox, ...). */
  isPrimary?: boolean;
}

export interface OpsanioPoc {
  wantPoc: boolean;
  allowedAsRefCustomer: boolean;
  pocSuccess: boolean;
}

/**
 * Pre-sales scoping / onboarding answers. These live in the raw questionnaire
 * facts but are intentionally NOT part of OpsanioValues / the assembly site, so
 * the helm values.yaml is unaffected. getOpsanioScoping() derives this object
 * straight from appliance.content for display in the appliance dashboard.
 *
 * Source sections (content key prefixes, catalog root stripped):
 *   - general.information.*        -> poc (Reference / PoC flags)
 *   - scanners.general.*           -> scanners (security + sizing scoping)
 *   - networks.netzwerk_fragen.*   -> network (network inventory scoping)
 *   - comment.comment_<n>          -> notes (free-text questions/comments)
 */
export interface OpsanioScopingSecurity {
  /** EDR products (open multi-select option keys). */
  edr: string[];
  /** Email-security products. */
  emailSecurity: string[];
  /** Browser-security products. */
  browserSecurity: string[];
  /** Microsoft 365 in use? */
  m365?: boolean;
}

export interface OpsanioScopingServers {
  /** Number of physical servers. */
  physicalCount?: number;
  /** Operating systems on the physical servers (option keys). */
  physicalOs: string[];
  /** Number of virtual servers. */
  virtualCount?: number;
  /** Virtualization platforms (option keys). */
  virtualPlatform: string[];
}

export interface OpsanioScopingScanners {
  security: OpsanioScopingSecurity;
  servers: OpsanioScopingServers;
  /** Number of endpoints. */
  endpoints?: number;
  /** Number of users. */
  users?: number;
  /** Number of log sources (Log-Quellen). */
  logSources?: number;
  /** Logging of applications enabled? */
  appLogging?: boolean;
}

export interface OpsanioScopingNetwork {
  /** Firewall vendors/products (option keys). */
  firewalls: string[];
  /** Switch vendors/products (option keys). */
  switches: string[];
  /** Fibre-channel vs. copper uplinks (Anbindung FC vs. Kupfer). */
  fcVsCopper?: boolean;
  /** Free-text: further network components (Weitere Netzwerkkomponenten). */
  additionalComponents?: string;
  /** Separate VLANs / zones in use? */
  separateVlansZones?: boolean;
  /** Flow-data collection desired? */
  collectFlowData?: boolean;
}

export interface OpsanioScopingNote {
  /** Source content key, e.g. "comment.comment_0". */
  id: string;
  /** Markdown body of the question/comment. */
  text: string;
  /** Author (set on save). */
  user?: string;
  commentType?: "question" | "note";
  /** ISO timestamp the entry was created. */
  createTime?: string;
}

export interface OpsanioScoping {
  poc: OpsanioPoc;
  scanners: OpsanioScopingScanners;
  network: OpsanioScopingNetwork;
  notes: OpsanioScopingNote[];
  /**
   * Every site entered in the Montagestandort form (cpe.location). The primary
   * site is also collapsed into common.location for the helm values; this array
   * carries all sites (primary first) so no location data is lost.
   */
  locations: OpsanioLocationValue[];
}
export interface OpsanioAssemblySite {
  contact: string;
  location: OpsanioLocationValue;
  switch: {
    useTwo: boolean;
    portsFree: boolean;
  };
  rack: {
    airGaped: boolean;
    twoRoom: boolean;
    huFree: boolean;
  };
  counts: {
    /**
     * Nodes that actually exist, i.e. `server.appliance.length`. This is the
     * number charts must size against - it drops when a node is flagged as
     * removed, without the surviving nodes being renumbered.
     */
    agents: number;
    /**
     * Node slots ever allocated (the raw `number_of_agents` answer), including
     * the slots held by removed nodes. Only for reporting - a new node is added
     * by raising this, which appends a slot rather than reusing a removed one.
     */
    agentSlots: number;
    server: number;
  };
  metadata: OpsanioMetadata;
  general: OpsanioGeneral;
}
export interface OpsanioSharedNetworkValues {
  ip?: string;
  mac?: string;
}

export interface OpsanioDashboardOidc {
  useAutonubil: boolean;
  enabled: boolean;
  redirect: string;
  client: {
    id: string;
    secret: string;
  };
  scope: string;
  connect: string;
}

export interface OpsanioLdap {
  enabled: boolean;
  ldaps: boolean;
  clientAuth: boolean;
  startTls: boolean;
  verifyHostnames: boolean;
  servers: Array<string>;
  domain: {
    name: string;
    dc: Array<string>;
  };
  bindUser: string;
  userbase: string;
  usersearch: string;
  usernameAttribute: string;
  customAttribute: Array<string>;
  roleBase: string;
  roleSearch: string;
  userRoleAttribute: string | null;
  userRolename: string;
  rolename: string;
  nestedRoles: boolean;
}

/** A single static DNS record captured in the customer network section. */
export interface OpsanioDnsEntry {
  /** Hostname / record name, e.g. "printer.customer.local". */
  name: string;
  /** DNS record type, e.g. "A", "AAAA", "CNAME". */
  type: string;
  /** Target IP address the record resolves to. */
  ip: string;
}

export interface OpsanioNetwork {
  name: string;
  cidr: string;
  vlan: number;
  /**
   * The site occupies two rooms. Set from cpe.tworoom.tworoomq, the question in
   * the location section, and true regardless of whether the rooms share a
   * network - it is a fact about the building, not the addressing.
   */
  isTwoRoom?: boolean;
  /**
   * The two rooms have DIFFERENT customer networks, so `subnets` carries one
   * entry per room and an appliance must be addressed on its own room's.
   *
   * Separate from isTwoRoom on purpose: two rooms on one stretched L2 share a
   * CIDR and a VLAN, and treating that like a split network would route traffic
   * through a gateway for peers that are genuinely on-link.
   */
  isDifferentNetwork?: boolean;
  /**
   * Every appliance's address falls inside the half its ROOM owns, so a node may
   * safely be addressed with that half's prefix instead of the parent's.
   *
   * A whole-site property, deliberately not per-node. Mixing prefixes across
   * peers is asymmetric routing: the /27 node treats a /26 peer as off-subnet
   * and routes it, while the /26 peer treats the first as on-link and ARPs.
   * False whenever ANY appliance is outside its room's half - including the
   * legacy allocation that handed halves out by agent index parity, which
   * interleaves them across rooms.
   */
  isRoomAligned?: boolean;
  /**
   * The room this subnet serves, on a network that is split per room.
   *
   * Only meaningful on entries of `subnets`, and only on the customer network:
   * an appliance's `zone` is matched against it to pick its CIDR and VLAN. Left
   * undefined on the parent network and on subnets that are not room-specific
   * (the CPE A/B halves, which are an address-space split rather than a
   * physical one).
   */
  zone?: OpsanioZone;
  gw?: string;
  dns?: string;
  ntp?: string;
  prefix?: number;
  shared?: OpsanioSharedNetworkValues;
  subnets?: Array<OpsanioNetwork>;
  /** Additional static DNS records to publish for this network. */
  dnsEntries?: Array<OpsanioDnsEntry>;
}
export interface OpsanioCpeNetwork extends OpsanioNetwork {
  isVm: boolean;
  useUefi: boolean;
}

export interface OpsanioFeatures {
  networkFlows: boolean;
  certificatesManagement: boolean;
  siem: boolean;
  openVas: boolean;
  nuclei: boolean;
  guacamole: boolean;
  compliance: boolean;
  webPush: {
    enabled: boolean;
    privateKey: string;
    publicKey: string;
  };
  opsanioAr: boolean;
  /**
   * The opsanio Agent. Defaults to `true`: it is part of the standard delivery,
   * so an unanswered questionnaire (old submissions) keeps it - only an
   * explicit No disables it.
   */
  opsanioAgent: boolean;
  bgp: boolean;
  ai: boolean;
  developer: boolean;
  previewAccess: boolean;
  /**
   * Cluster lifecycle is owned by Cluster API (CAPI), not by the per-stage
   * Terraform. When true the VPC has NO `200-k3-server` / `210-k3s` projects:
   * onboarding does not fork them, fork-freshness is not evaluated, and the
   * deploy step runs no Terraform pipelines. The credentials those projects used
   * to create are delivered from Vault in the values blob instead - notably the
   * Route53 `aws` credential (`vpc/.../aws`), which is read ONLY for CAPI-managed
   * VPCs. Defaults to false: an unanswered questionnaire stays Terraform-managed.
   */
  manageViaCapi: boolean;
  /**
   * The deployment is published under the customer's own domain
   * (`customerSettings.customerTld`) with a customer-supplied wildcard
   * certificate, instead of under `<vpc>.exanio.net` with ours. Defaults to
   * false; the TLD is emitted as "" while it is.
   */
  customDomain: boolean;
  /**
   * most installs from the pinned, signed APT suite of the wave's
   * `versions.<env>.apt_ring` (opsanio-helm-values renders `aptDists` and
   * `config.apt` into most-values). Defaults to false: an appliance stays
   * unpinned until it is switched on deliberately.
   */
  aptDists: boolean;
  /**
   * OpenSearch data tiers. A `false` tier is rendered with replicas: 0, dropped
   * from `gateway.expected_data_nodes` and removed from the temp allocation
   * include list - the release stays installed, PVCs and data survive, and the
   * change is reversible. Both default to `true`; only an explicit No disables.
   */
  opensearchTiers: {
    warm: boolean;
    cold: boolean;
  };
}
/**
 * A single severity-cut step for the events (wazuh-alerts) lifecycle streamer.
 * At `forceMergeAge`, alerts whose `rule.level` is below `keepLevel` are dropped
 * (compliance groups excepted). Multiple steps model a descending-volume ladder,
 * e.g. `[{forceMergeAge:"14d",keepLevel:3},{forceMergeAge:"90d",keepLevel:7}]`.
 */
export interface OpsanioSeverityCut {
  forceMergeAge: string;
  keepLevel: number;
}

/**
 * Events retention - `opensearch-wazuh-lifecycle` streamer (always active).
 * All ages are duration strings (`"365d"`, `"6h"`) as consumed by the chart.
 */
export interface OpsanioEventsRetention {
  /** Whether the lifecycle streamer actively manages the index (off by default). */
  manageLifecycle: boolean;
  /** Severity cut ladder (forceMergeAge / keepLevel steps). */
  severityCuts: OpsanioSeverityCut[];
  /** Final overall retention for security-alert history (compliance-driven). */
  retentionAge: string;
  /** Compliance frameworks archived forever - never cut or deleted. */
  keepComplianceGroups: string[];
  /** Absolute ceiling backstop; defaults to 2× `retentionAge`. */
  hardCapAge: string;
  /** Index pattern the streamer sweeps. */
  indexPattern: string;
  /** Maintenance sweep cadence. */
  maintenanceInterval: string;
}

/**
 * A managed index instance (raw vs. merged/aggregate) for the assets/flows
 * lifecycle ingesters. Ages are duration strings.
 */
export interface OpsanioLifecycleInstance {
  retentionAge: string;
  forceMergeAge: string;
}

/**
 * Devices retention - `opensearch-assets` ingester. Opt-in: when
 * `manageLifecycle` is false the indices stay on the legacy shared policy
 * (effectively unmanaged).
 */
export interface OpsanioDevicesRetention {
  manageLifecycle: boolean;
  /** Merged/codex (historical inventory) instance. */
  merged: OpsanioLifecycleInstance;
  /** Raw per-scan instance. */
  raw: OpsanioLifecycleInstance;
}

/**
 * Flows retention - `opensearch-flows` ingester. Opt-in like devices. NOTE:
 * the flows lifecycle opt-in is not honored by the chart yet; these values are
 * captured now so intake is complete once the chart support lands.
 */
export interface OpsanioFlowsRetention {
  manageLifecycle: boolean;
  /** Aggregated/summarized flow-trend instance. */
  aggregate: OpsanioLifecycleInstance;
  /** Raw per-flow (5-tuple) instance. */
  raw: OpsanioLifecycleInstance;
}

export interface OpsanioRetentions {
  events: OpsanioEventsRetention;
  devices: OpsanioDevicesRetention;
  flows: OpsanioFlowsRetention;
}

export interface OpsanioWireGuard {
  ip: string;
  dns: string;
  interface: string;
  port: number;
}

export interface OpsanioSwitch {
  serialNumber: string;
  mac: string;
  model: string;
}

/**
 * A DAC (Direct Attach Copper) cable selected from NetBox (role
 * "network-connection", device type sfp-10g-pc-02). Cables carry no MAC.
 */
export interface OpsanioDac {
  serialNumber: string;
  model: string;
}

export interface OpsanioHardwareSpecs {
  model: string;
  count: number;
}
export interface OpsanioHardwareProduct extends OpsanioHardwareSpecs {
  vendor: string;
}

export interface OpsanioHardwareCpu {
  model: string;
  cores: number;
  threads: number;
  ghz: number;
  serial: string;
}
export interface OpsanioHardwareRam {
  speed: number;
  /** Processor the module is attached to (from "PROC <n>" in the NetBox name). */
  proc: number;
  /** DIMM slot number, NOT a capacity - see `size` for how big the module is. */
  dimm: number;
  /** Module capacity in GB, parsed from the NetBox inventory item name. */
  size: number;
  part: string;
  type: string;
}
export type OpsanioHardwareStorageCapacityUnit = "GB" | "TB";
export type OpsanioHardwareStorageMediaType =
  | "SSD"
  | "HDD"
  | "NVMe"
  | "Unknown";

/**
 * One physical drive, as opsanio-logica records it on the NetBox inventory
 * item (see its `syncStorage` / `Drive.Describe`).
 *
 * Identity and wear live in two different places there and are read from both:
 * the part and serial numbers are fields on the item, everything else is
 * pipe-separated text in its description. A field the iLO did not report is
 * left empty (or `null` for the numbers) rather than defaulted - logica
 * deliberately stopped writing "N/A" and synthesised serials, so an empty
 * value here means "this drive reports none", which is a different statement
 * from zero.
 */
export interface OpsanioHardwareStorage {
  position: number;
  size: number;
  type: OpsanioHardwareStorageMediaType;
  controller: string;
  /** NetBox item label - the drive bay ("2I:1:5"), i.e. which one to pull. */
  label: string;
  /** iLO health: "OK", "Critical", … Empty when the run did not report one. */
  health: string;
  /** Drive model, recorded as the item's part number ("VK000480GWSRR"). */
  partNumber: string;
  serial: string;
  firmware: string;
  /**
   * Remaining SSD endurance in percent (Redfish
   * `PredictedMediaLifeLeftPercent`). `null` on drives that report none - a
   * spinning disk, or a drive too dead to answer - never 0, which would read
   * as "worn out".
   */
  lifeLeftPct: number | null;
  powerOnHours: number | null;
  temperatureC: number | null;
  /** The drive's own SMART prediction that it is about to fail. */
  failurePredicted: boolean;
  /** Why the drive is unhealthy, e.g. "StorageDevice.1.1.DriveFailure". */
  statusReason: string;
}
export interface OpsanioHardwareNic {
  speed: number;
  mac: string;
}
/**
 * One selected NetBox server, with its own CPU / RAM / NIC / disk inventory.
 * Several servers may be picked in Hardware > Server; the flat fields on
 * `OpsanioHardware` describe only one of them, so anything that needs the spec
 * *per node* (the generated appliance documentation, for one) reads this.
 */
export interface OpsanioHardwareDevice {
  /** NetBox device id. */
  id: string;
  /** NetBox device name - the node hostname where one is set. */
  name: string;
  serialNumber: string;
  memory: Array<OpsanioHardwareRam>;
  storage: {
    size: number;
    type: OpsanioHardwareStorageMediaType;
    disks: Array<OpsanioHardwareStorage>;
  };
  nic: Array<OpsanioHardwareNic>;
  cpu: Array<OpsanioHardwareCpu>;
}

export interface OpsanioHardware {
  /* product: OpsanioHardwareProduct; */
  memory: Array<OpsanioHardwareRam>;
  storage: {
    size: number;
    type: OpsanioHardwareStorageMediaType;
    disks: Array<OpsanioHardwareStorage>;
  };
  nic: Array<OpsanioHardwareNic>;
  cpu: Array<OpsanioHardwareCpu>;
  /**
   * Per-server breakdown, one entry per device selected in Hardware > Server.
   * The flat fields above are kept for existing consumers (helm values, storage
   * layout, dashboard) and describe the last device only.
   */
  devices: Array<OpsanioHardwareDevice>;
}

export interface OpsanioStorageConfRaid {
  system_size: number;
}

export interface OpsanioStorageConfDisk {
  devices: Array<string>;
  primaryDisk: string;
  efiSize: number;
  bootSize: number;
}

export interface OpsanioStorageConfSys {
  name: string;
  size: number;
  mount: string;
  fstype: string;
  options: string;
}

export interface OpsanioStorageConf {
  disks: OpsanioStorageConfDisk;
  raid: OpsanioStorageConfRaid;
  systemVolume: Record<string, Record<string, OpsanioStorageConfSys>>;
  dataVolume: Record<string, Record<string, OpsanioStorageConfSys>>;
  volumeGroups: any;
  encryption: OpsanioEncryption;
}

export interface OpsanioAgentBgp {
  enabled: boolean;
  description: string;
  cidr: string;
  as: {
    local: number;
    neighbor: number;
  };
  ips: {
    local: string;
    neighbor: string;
  };
  interfaces: Array<string>;
}
/**
 * Which room an appliance stands in, on a two-room site.
 *
 * A two-room site splits the customer network: each room has its own customer
 * CIDR and its own customer VLAN, and an appliance can only be addressed on the
 * one it is physically cabled to. Everything else - CPE, exanio, transfer -
 * stays cluster-wide.
 *
 * "A" is the default and the single-room answer. A site with
 * `networks.vlansZonen` false has every appliance in zone A and one customer
 * subnet, which is exactly the shape that existed before zones, so nothing
 * downstream has to special-case it.
 */
export type OpsanioZone = "A" | "B";

export interface OpsanioAppliance {
  hostname: string;
  description: string;
  offset: number;
  /**
   * The room this appliance is in. Selects which entry of
   * `networks.customer.subnets` gives it its customer CIDR and VLAN.
   *
   * Always set, always "A" on a single-room site, so consumers can read it
   * unconditionally rather than testing the two-room flag first.
   */
  zone: OpsanioZone;
  /**
   * The node was physically decommissioned. It keeps its questionnaire slot -
   * and therefore its IP/MAC offsets - so the remaining nodes are never
   * renumbered, but it is not deployed: `catalogActionOpsanio` moves it out of
   * `server.appliance` into `server.removedAppliance` before the values are
   * rendered.
   */
  removed: boolean;
  serialNumber: string;
  ips: {
    cpe: string;
    transfer: string;
    customer: string;
    redfish: string;
  };
  macs: {
    cpe: string;
    customer: string;
    one: string;
    two: string;
    redfish: string;
  };
  bgp: OpsanioAgentBgp;
}
export interface OpsanioOtherAppliance {
  ip: string;
}
export interface OpsanioServer extends OpsanioOtherAppliance {
  hostname: string;
}
export interface OpsanioServers {
  sharedIp: string;
  servers: Array<OpsanioServer>;
}
export interface OpsanioKula {
  hostname: string;
  ips: {
    transfer: string;
    host: string;
  };
}

export interface OpsanioNetworkScanner {
  name: string;
  cidr: string;
  interval: number;
  fingerprint: boolean;
  netbios: boolean;
  snmp: boolean;
  snmpV3: boolean;
  snmpSettingsUser: string;
  snmpSettingsPsw: string;
  snmpSettingsStr: string;
}
export interface OpsanioForiGateScanner {
  name: string;
  hostname: string;
  token: string;
  vdom: string;
  insecure: boolean;
}
export interface OpsanioBaramundiScanner {
  name: string;
  url: string;
  username: string;
  password: string;
  insecure: boolean;
}
export interface OpsanioIcingaTwoScanner {
  name: string;
  url: string;
  webuiurl: string;
  username: string;
  password: null;
  insecure: false;
}
export interface OpsanioIntuneScanner {
  name: string;
  tenantId: string;
  clientId: string;
  clientSecret: string;
}
export interface OpsanioNetdataScanner {
  name: string;
  searchDomain: string;
  url: string;
  username: string;
  password: string;
  insecure: boolean;
  parallelism: boolean;
}
export interface OpsanioOctopusScanner {
  name: string;
  url: string;
  workspaceId: string;
  apiToken: string;
  ocid: string;
}
export interface OpsanioProxmoxScanner {
  name: string;
  url: string;
  apiToken: string;
  insecure: boolean;
  distrustOsData: boolean;
}
export interface OpsanioSentinalOneScanner {
  name: string;
  customerDomain: string;
  insecure: boolean;
  proxy: string;
  apiKey: string;
}
export interface OpsanioScanners {
  network: Array<OpsanioNetworkScanner>;
  fortiGate: Array<OpsanioForiGateScanner>;
  baramundi: Array<OpsanioBaramundiScanner>;
  icingaTwo: Array<OpsanioIcingaTwoScanner>;
  intune: Array<OpsanioIntuneScanner>;
  netdata: Array<OpsanioNetdataScanner>;
  octopus: Array<OpsanioOctopusScanner>;
  proxmox: Array<OpsanioProxmoxScanner>;
  sentinalOne: Array<OpsanioSentinalOneScanner>;
}
export interface OpsanioDevices {
  /**
   * The nodes that actually exist. Charts size their workloads off this list
   * (and off `common.counts.agents`, which is its length), so nodes flagged as
   * removed are not in here.
   */
  appliance: Array<OpsanioAppliance>;
  /**
   * Decommissioned nodes, kept for the record only. Nothing deploys, PXE-boots,
   * DNS-maps or syncs to NetBox from this list - their slots stay occupied so a
   * later node never reuses their IP/MAC offsets.
   */
  removedAppliance: Array<OpsanioAppliance>;
  kubernetes: OpsanioServers;
  kula: OpsanioKula;
  probes: Array<OpsanioProbe>;
  switches: Array<OpsanioSwitch>;
  dac: Array<OpsanioDac>;
}

/**
 * A customer-site probe. Customer-facing network details (location, CIDR, VLAN,
 * IP) are entered by the customer; the CPE/Transfer IPs are derived from the
 * top of the CPE/Transfer CIDR pools (agents allocate from the bottom). The MAC
 * addresses, serial number and storage spec are internal-only hardware
 * attributes.
 */
export interface OpsanioProbe {
  index: number;
  locationName: string;
  serialNumber: string;
  customer: {
    cidr: string;
    vlan: string;
    gateway: string;
    dns: string;
    ntp: string;
  };
  ips: {
    cpe: string;
    transfer: string;
    customer: string;
  };
  macs: {
    one: string;
    two: string;
  };
  storage: {
    count: number;
    type: OpsanioHardwareStorageMediaType;
  };
  /**
   * Per-probe GitLab access for FluxCD bootstrap. A distinct GitLab project
   * access token (api scope, Maintainer role) on the Flux project, sufficient
   * for the probe to create the deploy token Flux needs to pull manifests.
   *
   * Injected server-side by the values endpoint (minted once, cached in Vault,
   * rotated near expiry) - it is NOT produced by the questionnaire parser, hence
   * optional. Never persisted in the questionnaire content.
   */
  flux?: OpsanioProbeFluxAccess;
}

export interface OpsanioProbeFluxAccess {
  /** GitLab base URL, e.g. https://gitlab.autonubil.net */
  url: string;
  /** Full path of the Flux project, e.g. opsanio/fluxcd/talpa */
  projectPath: string;
  /** Numeric GitLab project id of the Flux project. */
  projectId: number;
  /** Project access token value (api scope, Maintainer). */
  token: string;
  /** GitLab id of the access token, for rotation/revocation. */
  tokenId: number;
  /** Token expiry as YYYY-MM-DD. */
  expiresAt: string;
}
export interface OpsanioAgentDevices {
  appliance: OpsanioAppliance;
  kubernetes: OpsanioServers;
  kula: OpsanioKula;
  other: Array<OpsanioOtherAppliance>;
}

export interface OpsanioEncryption {
  key: string;
  cipher: string;
  key_size: number;
}

export interface OpsanioCustomerSettings {
  auth: {
    oidc: OpsanioDashboardOidc;
    ldap: OpsanioLdap;
  };
  /**
   * The customer's own top-level domain for the deployment, e.g.
   * `siem.customer.de`. Every ingress host is published as
   * `<service>.<customerTld>` instead of `<service>.<vpc>.exanio.net`, Traefik
   * matches its routers and header middlewares on it, and the customer-supplied
   * certificate has to cover `*.<customerTld>`.
   *
   * Empty whenever `features.customDomain` is off, which is the normal case -
   * the cluster then stays on `<vpc>.exanio.net`.
   */
  customerTld: string;
}

export type OpsanioWaves =
  | "dev"
  | "main"
  | "qa"
  | "wave1"
  | "wave2"
  | "wave3"
  | "debug";
export type OpsanioPullPolicy = "IfNotPresent" | "Always";


export interface OpsanioVersion {
  pullPolicy: OpsanioPullPolicy;
  opensearch_dashboard_version: string;
  wazuh_version: string;
  opsanio_plugin_version: string;
  opensearch_version: string;
  opsanio_plugin_tag: string;
  nats_processor_version: string;
  memgraph_version: string;
  mage_version: string;
  nats_version: string;
  promtail_version: string;
  cert_manager: string;
  longhorn: string;
  kubernetes_wodle: string;
  remotelog_wodle: string;
  coredns_version: string;
  codex: string;
  imap_wodle: string;
  osp_wodle: string;
  promtail_wodle: string;
  semaphrone: string;
  snmp_wodle: string;
  wazuh_rules: string;
  webhook_wodle: string;
  netflow_wodle: string;
  most: string;
  cardea: string;
  /**
   * APT ring this wave installs from - a free choice per wave, rendered only
   * for appliances with `features.aptDists`. Not synced from the parent wave:
   * the freshness gate would otherwise force every wave onto its parent's ring.
   */
  apt_ring: OpsanioAptRing | "";
}
export interface OpsanioVersions extends OpsanioVersion {
  parentWave: OpsanioWaves;
  createdAt?: string;
  updatedAt?: string;
}

export type OpsanioVersionMap = {
  [K in OpsanioWaves]?: OpsanioVersion;
};

export interface OpsanioValues {
  networks: Record<string, OpsanioNetwork>;
  scanners: OpsanioScanners;
  server: OpsanioDevices;
  common: OpsanioAssemblySite;
  storage: OpsanioStorageConf;
  wireguard: OpsanioWireGuard;
  hardware: OpsanioHardware;
  customerSettings: OpsanioCustomerSettings;
  vault: OpsanioVault;
  hosts: Record<string, string[]>;
  external_ips: Array<string>;
  opensearch_security_openid_enabled: boolean;
  environment: OpsanioWaves;
  versions: OpsanioVersionMap;
  node_size: OpsanioSizing;
  service_model: OpsanioServiceModel;
  sizing: OpsanioSizeValues;
  features: OpsanioFeatures;
  retentions: OpsanioRetentions;
  /**
   * Pre-sales scoping / onboarding answers (PoC flags, scanner & network
   * scoping, notes, all locations). Additive: helm templates ignore keys they
   * don't reference, so existing values.yaml consumers are unaffected.
   */
  scoping: OpsanioScoping;
  /**
   * Container-hardening rollout switches (`hardening.codex.fixPerms`, ...), set
   * per cluster in the internal "Hardening" section. Left out entirely when
   * nothing is selected, so an unconfigured cluster renders identical values.
   */
  hardening?: OpsanioHardening;
  /**
   * Pangolin appliance-management access. The shared admin provisioning key is
   * read from Vault at render time; omitted when Vault is unauthenticated or the
   * secret is missing, so consumers must treat it as optional.
   */
  pangolin?: OpsanioPangolin;
  /**
   * Per-VPC Route53 credentials (cert-manager DNS-01, external-dns), injected
   * from Vault (`vpc/.../aws`) at render time. Read ONLY when
   * `features.manageViaCapi` is true - on a Terraform-managed VPC the 210-k3s
   * Terraform owns these secrets, so anketa never reads the Vault path. Empty
   * strings when Vault is unauthenticated or the secret is missing.
   */
  aws?: OpsanioAwsCredentials;
  /**
   * Longhorn backup-bucket credentials, injected from Vault
   * (`vpc/.../backups_bucket`). Verbatim Vault shape. Optional.
   */
  backups_bucket?: OpsanioBackupBucket;
  /**
   * OpenSearch security config (LDAP/OIDC), injected verbatim from Vault
   * (`vpc/.../opensearch_security`) - the exact structure the 210-k3s Terraform
   * read. Optional; omitted when Vault is unauthenticated or the secret is missing.
   */
  opensearch_security?: OpsanioOpensearchSecurity;
  /**
   * Kubernetes OIDC connection config. The Keycloak client is created on render
   * (create-if-missing), its secret persisted to Vault, and the connection details
   * returned here so the chart renders the `oidc-connection-data` secret. Replaces
   * 210-k3s oidc.tf. Empty strings when Keycloak/Vault provisioning did not run.
   */
  oidc?: OpsanioOidc;
  /**
   * AI/ML gateway settings: spend budgets, cost-alert destinations and gateway
   * access. Read ONLY when `features.ai` is true - the whole `opsanio-helm-ai`
   * HelmRelease (and therefore every consumer of this block) is gated on that
   * switch, so an AI-less cluster never renders it.
   *
   * Always emitted, so `helm template` sees a complete block rather than having
   * to fall back to chart defaults it cannot know the customer agreed to.
   */
  ai: OpsanioAi;
}

/**
 * Spend limits for the AI gateway, in EURO. Not dollars: the model costs in the
 * chart are declared in euro already (`input_cost_per_token: 0.00000075` is
 * €0.75/1M), so LiteLLM's ledger, these caps and the customer's invoice are one
 * currency. The literal "$" LiteLLM prints in its own admin UI is cosmetic and
 * wrong - convert nothing.
 *
 * The three windows run CONCURRENTLY on one entity: LiteLLM tracks and resets
 * each independently, so a daily, a weekly and a monthly cap coexist without
 * nesting them across organisation, team and key.
 *
 * One gateway is deployed per customer VPC, so the budget owner IS the VPC -
 * the windows hang off the `platform` team the chart's bootstrap Job creates in
 * that cluster, and there is no cross-customer hierarchy to model.
 *
 * The team rather than the organization: organizations are a LiteLLM enterprise
 * feature, so without a licence an org budget is neither manageable nor visible.
 * `platform` is the only team in that org and every key belongs to it, so the
 * effect is the same - the whole gateway.
 *
 * The MONTHLY cap is the anchor: it is what an operator agrees with a customer,
 * and the daily and weekly windows are derived from it (see OpsanioAiBudgets).
 */
export interface OpsanioAiBudgetWindow {
  /** Hard cap in euro. 0 or absent leaves the window uncapped. */
  limit: number;
  /**
   * Soft threshold in euro: an alert fires here while requests still succeed.
   * Must be below `limit` to be useful - at or above it, the warning arrives
   * only once requests are already being rejected.
   */
  soft: number;
  /**
   * `true` means the window REJECTS at its own cap. `false` means it only warns.
   *
   * A non-enforcing window is NOT sent to LiteLLM at all, because LiteLLM cannot
   * express one: `budget_limits` entries take only a duration and a `max_budget`
   * (a `soft_budget` inside an entry is silently dropped, and an entry without
   * `max_budget` is a 422 that rejects every other window with it), and
   * `soft_budget` is a single entity-level field rather than per window.
   *
   * The early warning therefore comes from alerting on the enforced budget:
   * `threshold_crossed` at LiteLLM's fixed 85% and 95%, plus the monthly `soft`
   * figure. `projected_limit_exceeded` does NOT help here - it is key-only.
   *
   * Deliberately asymmetric across the three windows: only the MONTHLY one
   * rejects by default. That is the figure actually agreed with the customer,
   * so it is the one that should hold. The daily and weekly windows are DERIVED
   * from it (monthly/30 and monthly/4) to catch a runaway day or week early -
   * and a limit nobody negotiated has no business taking AI away, so those
   * alert instead.
   */
  enforce: boolean;
}

export interface OpsanioAiBudgets {
  enabled: boolean;
  /**
   * Derived from the monthly cap (monthly/30) unless answered explicitly. An
   * early-warning window, not a separately negotiated figure.
   */
  daily: OpsanioAiBudgetWindow;
  /** Derived from the monthly cap (monthly/4) unless answered explicitly. */
  weekly: OpsanioAiBudgetWindow;
  /**
   * Monthly window. Rendered as `1mo`, never `30d`: it resets on the first of
   * the month, which is how the customer reads their bill, whereas 30d drifts
   * against it.
   */
  monthly: OpsanioAiBudgetWindow;
}

export interface OpsanioAiAlerting {
  enabled: boolean;
  /**
   * Where this VPC's budget alerts are posted. RocketChat's incoming hooks accept
   * Slack-shaped payloads verbatim, so LiteLLM's "slack" channel is pointed
   * straight at it with no adapter.
   *
   * Two sources, in order: the questionnaire's per-VPC "Cost alert webhook URL"
   * answer, then the admin-managed default (Admin > AI cost alerts, falling back
   * to `AI_ALERT_WEBHOOK_URL` and then the operator hook). `getAiSettings` carries
   * the answer and `enrichWithAiAlerting` fills a blank one.
   *
   * Always delivered to the cluster as env from a Secret, never into the config
   * ConfigMap - the URL is a bearer credential. Empty disables the channel.
   */
  rocketChatWebhookUrl: string;
  /**
   * anketa's own receiver (`POST /api/v2/ai/budget-alert`) for LiteLLM's
   * structured `webhook` channel. Empty disables it, which also means no
   * per-customer fan-out happens.
   */
  anketaWebhookUrl: string;
  /** Periodic cost digest, e.g. `7d`. Empty disables the digest. */
  reportFrequency: string;
  email: {
    enabled: boolean;
    host: string;
    port: number;
    username: string;
    password: string;
    senderEmail: string;
    receiverEmails: Array<string>;
  };
}

/**
 * LiteLLM's own Keycloak login for the admin UI.
 *
 * Access control deliberately does NOT live at the Pangolin edge: that resource
 * is published as a plain tunnel, and LiteLLM authenticates the user against
 * Keycloak itself and then maps `roleClaim` onto its own role. A Pangolin policy
 * can only say "in" or "out"; LiteLLM can tell a gateway admin from someone who
 * may read their own spend and nothing else.
 *
 * anketa provisions the `<site>-litellm` Keycloak client, gives it the
 * `proxy_admin` / `internal_user` client roles, binds those to the
 * `ai-admins` / `ai-users` groups, and maps them into `roleClaim` - so Keycloak
 * group membership is what decides privilege inside LiteLLM.
 *
 * LIMITS: Admin-UI SSO is free for up to 5 users (LiteLLM >= 1.76.0); beyond
 * that it needs an enterprise licence. This covers the UI only - API callers
 * still use virtual keys, because presenting a Keycloak JWT to the API
 * (`litellm_jwtauth`) is enterprise-only.
 */
export interface OpsanioAiOidc {
  enabled: boolean;
  /** Realm issuer, e.g. `https://keycloak.autonubil.net/auth/realms/autonubil`. */
  issuer: string;
  clientId: string;
  clientSecret: string;
  scope: string;
  /** Claim carrying the LiteLLM role name. */
  roleClaim: string;
  /**
   * Public URL the proxy answers on. The registered redirect is
   * `<proxyBaseUrl>/sso/callback`, so this MUST match what the chart renders as
   * `PROXY_BASE_URL` or Keycloak rejects the login.
   */
  proxyBaseUrl: string;
}

export interface OpsanioAiAccess {
  oidc: OpsanioAiOidc;
}

export interface OpsanioAi {
  budgets: OpsanioAiBudgets;
  alerting: OpsanioAiAlerting;
  /**
   * Gateway access. OPTIONAL because it exists only after render-time
   * enrichment - the questionnaire has no facts for it, the same reason
   * `OpsanioValues.oidc` and `.pangolin` are optional. Only the OIDC block is
   * emitted; the chart's own `usersFromContacts`, `publishUi` and `ui` defaults
   * survive the deep merge.
   */
  access?: OpsanioAiAccess;
}

export interface OpsanioOidc {
  issuerUrl: string;
  clientId: string;
  clientSecret: string;
  scopes: string;
}

export interface OpsanioVault {
  address: string;
  secret: string;
  role: string;
}

export interface OpsanioAwsCredentials {
  accessKeyID: string;
  secretAccessKey: string;
  region: string;
  /**
   * Route53 hosted zone the cluster's records live in. Consumed by the chart's
   * `aws-route53-clusterissuer` secret and the `clusterissuer-values` ConfigMap;
   * empty when the Vault secret does not carry it.
   */
  hostedZoneID: string;
}

export interface OpsanioBackupBucket {
  aws_access_key_id: string;
  aws_secret_access_key: string;
  aws_default_region: string;
  title?: string;
  username?: string;
}

/**
 * OpenSearch security config stored verbatim in Vault at
 * `vpc/.../opensearch_security` (keys enable/auth/ui/openid/backend/oidc_root_ca/ldap)
 * and passed through unchanged - same structure the Terraform consumed.
 */
export type OpsanioOpensearchSecurity = Record<string, unknown>;

export interface OpsanioPangolin {
  provisioning_key: string;
}

export interface OpsanioAgentValues {
  networks: Record<string, OpsanioNetwork>;
  scanners: OpsanioScanners;
  server: OpsanioAgentDevices;
  common: OpsanioAssemblySite;
  storage: OpsanioStorageConf;
  wireguard: OpsanioWireGuard;
}

export interface OpsanioMustacheValues {
  project: string;
  owner: string;
  stage: string;
  vpcName: string;
}

export interface RocketChatAttachment {
  color?: string;
  text?: string;
  ts?: string;
  thumb_url?: string;
  message_link?: string;
  collapsed?: boolean;
  author_name?: string;
  author_link?: string;
  author_icon?: string;
  title?: string;
  title_link?: string;
  title_link_download?: boolean;
  image_url?: string;
  audio_url?: string;
  video_url?: string;
  fields?: Array<{
    short?: boolean;
    title: string;
    value: string;
  }>;
}

export interface RocketChatMessage {
  text?: string;
  username?: string;
  icon_url?: string;
  icon_emoji?: string;
  attachments?: RocketChatAttachment[];
  alias?: string;
  avatar?: string;
}

export interface RocketChatWebhookOptions {
  webhookUrl: string;
  message: RocketChatMessage;
  timeout?: number;
  retries?: number;
}

export interface RocketChatResponse {
  success: boolean;
  status: number;
  data?: any;
  error?: string;
}

export interface OpsanioGitLabEndpoints {
  module: keyof OpsanioVersions;
  projectId: string;
}

/**
 * The git refs the version overview (`/opsanio/version`) tracks per project.
 * `dev`/`main` are branches; `major`/`minor` are the *floating* semver tags the
 * release pipeline re-points (`v1`, `v1.12`), `patch` is the immutable, highest
 * exact tag (`v1.12.86`).
 *
 * `default` is a catch-all, filled in only when a project's default branch is
 * neither `dev` nor `main` (`master`, `autonubil`, `feat/inspector-overhaul`, ...)
 * so such a project still shows something rather than five empty cells.
 */
export type OpsanioVersionRefKind =
  | "dev"
  | "main"
  | "default"
  | "major"
  | "minor"
  | "patch";

export const OPSANIO_VERSION_REF_KINDS: OpsanioVersionRefKind[] = [
  "dev",
  "main",
  "default",
  "major",
  "minor",
  "patch",
];

/** The latest GitLab CI pipeline on a ref. */
export interface OpsanioRefPipeline {
  id: number;
  iid: number;
  status: string;
  source: string;
  sha: string;
  ref: string;
  web_url: string;
  created_at: string;
  updated_at: string;
}

/** The commit a tracked ref currently points at. */
export interface OpsanioRefCommit {
  id: string;
  short_id: string;
  title: string;
  author_name: string;
  committed_date: string;
  web_url?: string;
}

/** One tracked channel of one module, with its latest pipeline. */
export interface OpsanioVersionRef {
  kind: OpsanioVersionRefKind;
  /** Whether the channel is a branch (`dev`, `main`) or a tag. */
  type: "branch" | "tag";
  /**
   * The channel label: `dev`, `main`, `v1`, `v1.12`, `v1.12.86`. Null when the
   * module has no release on this channel at all.
   */
  label: string | null;
  /**
   * The git ref actually resolved, i.e. the one the pipeline below ran on.
   * Equals `label` for branches and for floating tags that really exist. The
   * opsanio repos carry no bare `vX` tag, so the major channel usually resolves
   * to the newest tag in that major series instead - `label: "v1"` with
   * `ref: "v1.12.86"`. Null when nothing could be resolved.
   */
  ref: string | null;
  /** True when `ref` had to be derived because `label` is not itself a tag. */
  derived: boolean;
  commit: OpsanioRefCommit | null;
  /** Latest pipeline on `ref`, or null when the ref has none / does not exist. */
  pipeline: OpsanioRefPipeline | null;
}

/** Every tracked ref of one project in the opsanio GitLab group. */
export interface OpsanioModuleVersions {
  /** Project slug - the last segment of `projectPath` (`codex-rimor`). */
  module: string;
  projectId: string;
  /** `opsanio/codex-rimor` - empty when the project lookup failed. */
  projectPath: string;
  /**
   * Namespace the project sits in: `opsanio`, `opsanio/helm`,
   * `opsanio/rimor-plugins/scanners`. Drives the group column + filter.
   */
  group: string;
  webUrl: string;
  defaultBranch: string;
  /** GitLab `last_activity_at`, i.e. what the activity window filters on. */
  lastActivityAt: string;
  archived: boolean;
  /**
   * True for the projects the wave update manager ships a version field for
   * (`OPSANIO_MODULE_PROJECTS`). These are pinned into the response even when
   * they fall outside the activity window or are archived in GitLab.
   */
  waveTracked: boolean;
  refs: OpsanioVersionRef[];
  /** Set when the project could not be read at all; `refs` is then empty. */
  error?: string;
}

export interface OpsanioLatestVersionsResponse {
  generatedAt: string;
  /** The GitLab group the projects were discovered from. */
  groupId: number;
  /** Activity window in days that the discovery was filtered on. */
  windowDays: number;
  /** True when this response came from the server-side TTL cache. */
  cached: boolean;
  /** Projects discovered but skipped (pending deletion, ignored subgroups). */
  skipped: number;
  /** How many of those were dropped by the ignore list specifically. */
  ignored: number;
  /** Namespace prefixes this overview deliberately does not cover. */
  ignoredPaths: string[];
  /**
   * Wave-tracked modules left out because they are archived in GitLab. Worth
   * naming: the update manager still ships a version field for them, so their
   * absence from this page is a fact about GitLab, not about the wave.
   */
  excludedArchived: string[];
  modules: OpsanioModuleVersions[];
}

export interface GitLabTags {
  major: GitLabTag;
  latestMinor?: GitLabTag;
  latestPatch?: GitLabTag;
}
export interface GitLabTagsResponse {
  [major: string]: GitLabTags;
}

export interface GitLabTag {
  name: string;
  message: string | null;
  target: string;
  commit: {
    id: string;
    short_id: string;
    created_at: string;
    parent_ids: string[];
    title: string;
    message: string;
    author_name: string;
    author_email: string;
    authored_date: string;
    committer_name: string;
    committer_email: string;
    committed_date: string;
  };
  release: {
    tag_name: string;
    description: string;
    created_at: string;
    released_at: string;
    author: {
      id: number;
      name: string;
      username: string;
      state: string;
      avatar_url: string | null;
      web_url: string;
    };
  } | null;
  protected: boolean;
}

export interface GitLabUser {
  id: number;
  username: string;
  public_email: string;
  name: string;
  state: "active" | "blocked";
  locked: boolean;
  avatar_url: string;
  web_url: string;
}

export interface GitLabMergeApprove {
  user_has_approved: boolean;
  user_can_approve: boolean;
  approved: boolean;
  approved_by: { user: { id: number; username: string; name: string } }[];
}

export interface GitLabMerge {
  id: number;
  iid: number;
  project_id: number;
  title: string;
  description: string | null;
  state: "merged" | "opened" | "closed" | string;
  created_at: string;
  updated_at: string;
  merged_by?: GitLabUser;
  merge_user?: GitLabUser;
  merged_at?: string;
  closed_by?: GitLabUser | null;
  closed_at?: string | null;
  target_branch: string;
  source_branch: string;
  user_notes_count: number;
  upvotes: number;
  downvotes: number;
  author: GitLabUser;
  assignees: GitLabUser[];
  assignee?: GitLabUser;
  reviewers: GitLabUser[];
  source_project_id: number;
  target_project_id: number;
  labels: string[];
  draft: boolean;
  imported: boolean;
  imported_from: string;
  work_in_progress: boolean;
  milestone: any | null;
  merge_when_pipeline_succeeds: boolean;
  merge_status: string;
  detailed_merge_status: string;
  merge_after: string | null;
  sha: string;
  merge_commit_sha: string;
  squash_commit_sha: string | null;
  discussion_locked: boolean | null;
  should_remove_source_branch: boolean | null;
  force_remove_source_branch: boolean | null;
  prepared_at: string;
  reference: string;
  references: {
    short: string;
    relative: string;
    full: string;
  };
  web_url: string;
  time_stats: {
    time_estimate: number;
    total_time_spent: number;
    human_time_estimate: string | null;
    human_total_time_spent: string | null;
  };
  squash: boolean;
  squash_on_merge: boolean;
  task_completion_status: {
    count: number;
    completed_count: number;
  };
  has_conflicts: boolean;
  blocking_discussions_resolved: boolean;
  approve?: GitLabMergeApprove;
}

export interface OpsanioWaveCounts {
  catalog: string;
  questionnaire: string;
  wave: OpsanioWaves;
  name: string;
  parentWave: OpsanioWaves;
  deployState: OpsanioGitLabDeployState;
}

export interface OpsanioGitLabDeployState {
  validQuestioner: boolean; // Means that the customer have fill out all information that we can deploy
  projects: Array<GitLabProjectShort>; // Array with the three projects
}
export interface OpsanioWaveMember extends OpsanioGitLabDeployState {
  name: string;
}

export interface GitLabProjectShort {
  id: number;
  name: string;
  web_url: string;
}

export interface GitLabProject extends GitLabProjectShort {
  service_desk_address: string;
  name_with_namespace: string;
  path: string;
  path_with_namespace: string;
  description: string | null;
  avatar_url: string | null;
  star_count: number;
  forks_count: number;
  creatavatar_urled_at: string;
  last_activity_at: string;
  default_branch: string;
  visibility: "private" | "internal" | "public";
  ssh_url_to_repo: string;
  http_url_to_repo: string;
  namespace: {
    id: number;
    name: string;
    path: string;
    kind: "group" | "user";
    full_path: string;
  };
  tag_list: string[];
  archived: boolean;
  topics?: string[];
  [key: string]: any;
}

export interface GitLabLabel {
  id?: number;
  name: string;
  color?: string;
  description?: string;
}

export interface GitLabIssueCreateOptions {
  projectId: string | number;
  title: string;
  description?: string;
  assignee?: { id: number };
  issueType?: "issue" | "incident" | "test_case" | "task";
  startDate?: Date;
  dueDate?: Date;
  labels?: string[];
  confidential?: boolean;
  milestone?: number;
  weight?: number;
  isServiceDesk?: boolean;
  serviceDeskReplyTo?: string;
  discussionLocked?: boolean;
}

export interface GitLabIssue {
  id: number;
  iid: number;
  project_id: number;
  title: string;
  description?: string;
  state: "opened" | "closed";
  created_at: string;
  updated_at: string;
  closed_at?: string | null;
  closed_by?: GitLabUser | null;

  author: GitLabUser;
  assignees?: GitLabUser[];
  assignee?: GitLabUser | null;

  labels: string[] | GitLabLabel[];
  milestone?: {
    id: number;
    title: string;
    due_date?: string;
    start_date?: string;
  } | null;

  web_url: string;
  references?: {
    short: string;
    relative: string;
    full: string;
  };

  time_stats?: {
    time_estimate: number;
    total_time_spent: number;
    human_time_estimate?: string | null;
    human_total_time_spent?: string | null;
  };

  task_completion_status?: {
    count: number;
    completed_count: number;
  };

  due_date?: string | null;
  confidential?: boolean;
  discussion_locked?: boolean;
  weight?: number | null;
  epic_iid?: number | null;
  has_tasks?: boolean;
  type?: "ISSUE" | "TASK";
  service_desk_reply_to?: string;
}

export interface GitLabNote {
  id: number;
  body: string;
  author: {
    id: number;
    name: string;
    username: string;
    avatar_url?: string;
    web_url?: string;
  };
  created_at: string;
  updated_at: string;
  system: boolean;
  noteable_id?: number;
  noteable_type?: string;
  confidential?: boolean;
}

/**
 * PRTG monitoring types. Devices/sensors are sourced from the PRTG table API
 * (`/api/table.json?content=devices|sensors`) for a single appliance instance,
 * scoped to the `opsanio` group and the instance's own subgroup. Matched to the
 * resource by `common.metadata.name` == PRTG device/group name. See
 * functions/prtg and routes/v2/opsanio.ts (`/prtg/instance/:name`).
 */
export type PrtgStatusCategory =
  | "up"
  | "down"
  | "warning"
  | "paused"
  | "unusual"
  | "unknown";

export interface PrtgStatusCounts {
  up: number;
  down: number;
  warning: number;
  paused: number;
  unusual: number;
  unknown: number;
  total: number;
}

export interface PrtgSensor {
  objid: number;
  /** Sensor name. */
  sensor: string;
  /** Parent device name. */
  device: string;
  /** Formatted PRTG status text (e.g. "Up", "Down", "Warning"). */
  status: string;
  /** Raw numeric PRTG status code. */
  statusRaw: number;
  /** Normalized status category derived from `statusRaw`. */
  category: PrtgStatusCategory;
  /** Formatted last sensor value. */
  lastValue: string;
  /** Formatted last-checked timestamp. */
  lastCheck: string;
  /** Status / error message. */
  message: string;
  priority?: number;
}

export interface PrtgDevice {
  objid: number;
  /** Device name (equals the resource's `common.metadata.name` when matched). */
  device: string;
  /** Host / IP address. */
  host: string;
  /** Immediate parent group name (the instance subgroup). */
  group: string;
  probe?: string;
  /** Formatted PRTG device status text. */
  status: string;
  /** Raw numeric PRTG device status code. */
  statusRaw: number;
  /** Normalized status category derived from `statusRaw`. */
  category: PrtgStatusCategory;
  /** Device status / error message. */
  message: string;
  active: boolean;
  tags?: string;
  sensors: PrtgSensor[];
  /** Aggregate counts across this device's sensors. */
  counts: PrtgStatusCounts;
}

export interface PrtgInstanceStatus {
  /** The `common.metadata.name` the status was requested for. */
  instance: string;
  /** PRTG web UI base URL - build deep links as `${baseUrl}/device.htm?id=…`. */
  baseUrl: string;
  /** Resolved opsanio root group name the lookup was scoped to. */
  rootGroup: string;
  /** Resolved instance subgroup name, when one matched. */
  group?: string;
  /** PRTG object id of the resolved instance subgroup, for a group deep link. */
  groupId?: number;
  devices: PrtgDevice[];
  /** Aggregate counts across every matched device and its sensors. */
  counts: PrtgStatusCounts;
  /** ISO timestamp of when the data was fetched from PRTG. */
  fetchedAt: string;
}

/**
 * One channel of a node's cluster-readiness push sensor (readiness.sh, see
 * opsanio-helm-cluster-readiness `files/PRTG_CHANNELS.md`).
 */
export interface PrtgReadinessChannel {
  /** Channel name as pushed, e.g. "Nodefs", "NodeReachableAddress10.0.0.5". */
  name: string;
  /**
   * PRTG's raw value (`lastvalue_raw`). The formatted value is not usable for
   * every channel: GiB channels created before their unit existed render as
   * "0,0000453 MB", while the raw value is the correct 47.7.
   */
  value: number | null;
  /** PRTG's formatted value, kept for channels the UI does not know. */
  display: string;
}

export interface PrtgReadinessNode {
  /** PRTG device (one per Kubernetes node). */
  device: string;
  host: string;
  deviceId: number;
  sensorId: number;
  /** Status of the readiness sensor itself (e.g. down when pushes stop). */
  sensorCategory: PrtgStatusCategory;
  sensorMessage: string;
  channels: PrtgReadinessChannel[];
}

/** Cluster-readiness channels of every node of an instance. */
export interface PrtgReadiness {
  instance: string;
  baseUrl: string;
  fetchedAt: string;
  nodes: PrtgReadinessNode[];
}

/**
 * OTOBO (OTRS-based) ticket types. Tickets are read from the OTOBO REST Generic
 * Interface (`TicketSearch` then `TicketGet`) for a single appliance instance,
 * scoped to the instance's queue `<OTOBO_MASTER_QUEUE>::<common.metadata.name>`.
 * All OTOBO access happens on the backend with credentials read from the
 * environment - never exposed to the client. See functions/otobo and
 * routes/v2/opsanio.ts (`/otobo/instance/:name`).
 */
export type OtoboStateCategory = "open" | "closed";

export interface OtoboArticle {
  /** OTOBO article id. */
  ArticleID: number;
  /** Sender type: `agent` | `customer` | `system`. */
  SenderType?: string;
  From?: string;
  To?: string;
  Cc?: string;
  Subject?: string;
  Body?: string;
  /** OTOBO timestamp `YYYY-MM-DD HH:mm:ss`. */
  CreateTime: string;
  /** True when the article is internal/not visible to the customer. */
  IsVisibleForCustomer?: boolean;
}

export interface OtoboTicket {
  TicketID: number;
  TicketNumber: string;
  Title: string;
  /** Human state name, e.g. `open`, `closed successful`. */
  State: string;
  /** State grouping, e.g. `new`, `open`, `pending reminder`, `closed`. */
  StateType: string;
  /** Normalized open/closed bucket derived from `StateType`. */
  category: OtoboStateCategory;
  /** Full OTOBO queue name (`<master>::<instance>`). */
  Queue: string;
  Priority: string;
  /** Owner agent login/name. */
  Owner?: string;
  /** Responsible agent login/name. */
  Responsible?: string;
  CustomerID?: string;
  CustomerUserID?: string;
  /** OTOBO timestamps `YYYY-MM-DD HH:mm:ss`. */
  Created: string;
  Changed: string;
  /** Deep link into the OTOBO agent UI for this ticket, when resolvable. */
  web_url?: string;
  /** Populated only by the per-ticket detail endpoint. */
  Article?: OtoboArticle[];
}

export interface OtoboInstanceTickets {
  /** The `common.metadata.name` the tickets were requested for. */
  instance: string;
  /** Full OTOBO queue name the search was scoped to. */
  queue: string;
  /** Whether `queue` actually exists in OTOBO. When false, `tickets` is empty
   *  and the UI should report that the queue has not been created yet. */
  queueExists: boolean;
  /** OTOBO web UI base URL, when resolvable, for building ticket deep links. */
  baseUrl?: string;
  tickets: OtoboTicket[];
  /** ISO timestamp of when the data was fetched from OTOBO. */
  fetchedAt: string;
}

/**
 * Sentry User Feedback for an appliance instance. Read on the backend from the
 * Sentry issues API (`issue.category:feedback`) scoped to the Sentry
 * environment(s) matching the instance's `common.metadata.name` (e.g.
 * `dashboard.<name>.exanio.net`). See functions/sentry and routes/v2/opsanio.ts
 * (`/sentry/feedback/instance/:name`).
 */
export interface SentryFeedback {
  /** Sentry issue id. */
  id: string;
  /** Human short id, e.g. `OPSANIO-DASHBOARDS-PLUGIN-2KB`. */
  shortId: string;
  /** The feedback message text. */
  message: string;
  title: string;
  /** Sentry level: `info` | `warning` | `error` | … */
  level: string;
  /** Issue status: `unresolved` | `resolved` | `ignored`. */
  status: string;
  substatus?: string;
  /** ISO timestamps. */
  firstSeen: string;
  lastSeen: string;
  /** Number of occurrences. */
  count: number;
  /** Deep link into the Sentry feedback UI. */
  permalink: string;
  /** Originating Sentry project slug. */
  project?: string;
}

export interface SentryInstanceFeedback {
  /** The `common.metadata.name` the feedback was requested for. */
  instance: string;
  /** Sentry environments that matched the instance (filtered on). */
  environments: string[];
  feedback: SentryFeedback[];
  /** ISO timestamp of when the data was fetched from Sentry. */
  fetchedAt: string;
}

/**
 * Keycloak login statistics for an appliance's OIDC client, read from the
 * medocino realm's Admin REST API (LOGIN events grouped per user). Only
 * surfaced for the `main` and `qa` waves. See functions/keycloak-medocino and
 * routes/v2/opsanio.ts (`/keycloak/auths/:name`).
 */
export interface OpsanioKeycloakUserLogin {
  userId: string;
  /** Resolved Keycloak username, or "unknown" when the user lookup failed. */
  username: string;
  /** Number of LOGIN events for this user against the target client. */
  count: number;
  /** Epoch millis of the most recent login, or null when unknown. */
  lastLogin: number | null;
}

export interface OpsanioKeycloakLoginStats {
  /** The Keycloak clientId the stats are for (== appliance metadata.name). */
  client: string;
  /** Realm the client was looked up in. */
  realm: string;
  /** False when no client with this id exists in the realm. */
  clientFound: boolean;
  users: OpsanioKeycloakUserLogin[];
  /** Sum of all per-user login counts. */
  totalLogins: number;
  /** Number of distinct users that logged in. */
  uniqueUsers: number;
  /** ISO timestamp of when the data was fetched from Keycloak. */
  fetchedAt: string;
}

/**
 * Pangolin endpoints for an appliance's Kubernetes site (always named
 * `<common.metadata.name>`), read from the Pangolin Integration API. A
 * resource is an exposed service; its targets are the concrete backend
 * endpoints (ip:port) behind it. See functions/pangolin and routes/v2/opsanio.ts
 * (`/pangolin/endpoints/:name`).
 */
export interface OpsanioPangolinTarget {
  targetId: string | number;
  /** Backend host/IP the target points at. */
  ip: string;
  /** Backend port, or null when not applicable. */
  port: number | null;
  /** Connection method/protocol (http, https, tcp, udp, ...), or null. */
  method: string | null;
  /** Whether this target is enabled. */
  enabled: boolean;
}

export interface OpsanioPangolinResource {
  resourceId: string | number;
  /** Human-readable resource name. */
  name: string;
  /** The app it publishes, read from its `<app>-<site>.<domain>` domain. */
  app: string;
  /** Public URL (`https://<fullDomain>`), or null for non-HTTP. */
  url: string | null;
  /** Public domain the resource is served on, or null for non-HTTP. */
  fullDomain: string | null;
  /** Resource protocol/mode (http, ssh, tcp, ...), or null. */
  protocol: string | null;
  /** Whether the resource is enabled. */
  enabled: boolean;
  /** Concrete backend endpoints behind the resource. */
  targets: OpsanioPangolinTarget[];
}

/**
 * An app other sites of the fleet publish but this one does not, plus the
 * target a one-click create would clone from those instances.
 */
export interface OpsanioPangolinAppGap {
  app: string;
  /** How many sites of the fleet publish the app. */
  fleetSites: number;
  /** Backend target a create would use; null when none can be derived. */
  plannedTarget: OpsanioPangolinTarget | null;
}

export interface OpsanioPangolinEndpoints {
  /** The Pangolin site name the endpoints belong to (`<metadata.name>`). */
  site: string;
  /** Internal Pangolin site id, or null when the site was not found. */
  siteId: string | number | null;
  /** False when no site with this name exists in the org (not an error). */
  siteFound: boolean;
  /** Every resource published through the site - discovered, not configured. */
  resources: OpsanioPangolinResource[];
  /**
   * Standard apps (run on at least half of the fleet; AI-only apps when the AI
   * stack is enabled) the site lacks.
   */
  missing: OpsanioPangolinAppGap[];
  /** Less common fleet apps the site lacks - offered, not reported as gaps. */
  available: OpsanioPangolinAppGap[];
  /** Number of resources on the site. */
  resourceCount: number;
  /** Total number of targets across all resources. */
  targetCount: number;
  /** ISO timestamp of when the data was fetched from Pangolin. */
  fetchedAt: string;
}

// ---------------------------------------------------------------------------
// OPL (Offene-Punkte-Liste): the weekly Regeltermine of one appliance and the
// points raised in them. Backend: routes/v2/opsanio-opl.ts.
// Dates arrive as ISO strings over the wire.
// ---------------------------------------------------------------------------

export type OplMeetingStatus = "planned" | "held" | "cancelled";
/** Immutable over a point's lifetime. I = Information, A = Aufgabe, E = Entscheidung. */
export type OplItemType = "I" | "A" | "E";
export type OplItemStatus = "open" | "done" | "split" | "discarded";
export type OplItemPriority = "high" | "medium" | "low";
export type OplAttendeeSource = "keycloak" | "contact" | "manual";
export type OplAbsenceReason = "excused" | "unexcused";

/** Statuses that take a point off the carry-over list. */
export const OPL_CLOSED_STATUSES: readonly OplItemStatus[] = [
  "done",
  "split",
  "discarded",
];

export const isOplItemClosed = (status: OplItemStatus): boolean =>
  OPL_CLOSED_STATUSES.includes(status);

/** Types that require a due date and an owner. */
export const oplTypeRequiresCommitment = (type: OplItemType): boolean =>
  type !== "I";

export interface OplAttendee {
  id?: string;
  /** The only required identity - a participant need not have an account. */
  name: string;
  /** Only participants who have one receive the summary mail. */
  email?: string | null;
  /** Stable per-round identity: the address, or a slug of the name. */
  participantKey?: string;
  source?: OplAttendeeSource;
  present: boolean;
  /** Guests sit apart from the Stammbesetzung and are never listed as absent. */
  isGuest: boolean;
  organisation?: string | null;
  /** Partial attendance, wall-clock strings: "10:20" / "10:45". */
  fromTime?: string | null;
  untilTime?: string | null;
  absenceReason?: OplAbsenceReason | null;
  substitute?: string | null;
}

export interface OplMeeting {
  id: string;
  applianceName: string;
  /** Lockstep ordinal over rounds that took place. The unit visibility counts in. */
  sequence: number;
  /** Display label, e.g. "2026-KW33". Never a basis for arithmetic. */
  key: string;
  date: string;
  durationMinutes: number | null;
  title: string | null;
  /** Protokollführer. */
  recorder: string | null;
  moderator: string | null;
  /** Minutes, Markdown. Carried by the summary mail. */
  notes: string | null;
  status: OplMeetingStatus;
  summarySentAt: string | null;
  summarySentBy: string | null;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  attendees: OplAttendee[];
}

/** One line of a point's append-only log. Never edited, never deleted. */
export interface OplItemLogEntry {
  id: string;
  itemId: string;
  meetingId: string | null;
  entryDate: string;
  text: string;
  createdAt: string;
  createdBy: string;
}

/** The round references a point carries, as returned with it. */
export interface OplItemRoundRef {
  id: string;
  key: string;
  sequence: number;
  date: string;
}

export interface OplItem {
  id: string;
  applianceName: string;
  /** Human-readable, year-prefixed, never reused: "2026-047". */
  key: string;
  title: string;
  owner: string | null;
  /** Null for type I. */
  dueDate: string | null;
  type: OplItemType;
  priority: OplItemPriority;
  status: OplItemStatus;
  openedInMeetingId: string | null;
  closedInMeetingId: string | null;
  closedAt: string | null;
  /** Assigned to someone absent without a substitute. Shown in list and minutes. */
  assignedInAbsence: boolean;
  parentItemId: string | null;
  gitlabProjectId: number | null;
  gitlabIssueIid: number | null;
  gitlabIssueUrl: string | null;
  otoboTicketId: number | null;
  otoboTicketNumber: string | null;
  otoboTicketUrl: string | null;
  promotedAt: string | null;
  promotedBy: string | null;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  log: OplItemLogEntry[];
  openedIn?: OplItemRoundRef | null;
  closedIn?: OplItemRoundRef | null;
}

/** Where a default participant came from. */
export type OplContactRole = "technical" | "sales" | "customerTechnical";

/**
 * A participant proposed for a brand-new round, resolved from the appliance's
 * questionnaire contacts. Only ever a suggestion - the round owns its own list
 * once it exists.
 */
export interface OplDefaultParticipant {
  name: string;
  email?: string | null;
  source?: OplAttendeeSource;
  present?: boolean;
  isGuest?: boolean;
  role: OplContactRole;
}

/** Everything the OPL page needs, in one round trip. */
export interface OplBoard {
  applianceName: string;
  meetings: OplMeeting[];
  items: OplItem[];
  currentMeetingId: string | null;
  /** Seeds the first round's participant list; see functions/opl/participants.ts. */
  defaultParticipants: OplDefaultParticipant[];
}

/**
 * Outcome of a summary send. `sent: false` is a *successful* request whose mail
 * did not go out - the round is already committed and the UI reports a warning.
 */
export interface OplSummaryResult {
  sent: boolean;
  recipients: string[];
  sentAt?: string;
  warning?: string;
}

/** Result of promoting a point into a GitLab issue or an OTOBO ticket. */
export interface OplPromotionResult {
  item: OplItem;
  /** False when the external object exists but the reference could not be stored. */
  persisted: boolean;
  warning?: string;
}

// ---------------------------------------------------------------------------
// Provisioning inventory: everything anketa creates for one VPC, its current
// state, and how to create what is missing. Produced by
// functions/gitlab/onboarding/inventory.ts, rendered by the appliance flyout's
// Resources tab. Backend: routes/v2/opsanio.ts (`/resources/...`).
// ---------------------------------------------------------------------------

/**
 * State of one provisioned resource.
 *
 * `unknown` is deliberately NOT `missing`: it means the owning system could not
 * answer (no search API, or not configured on this anketa instance). The UI must
 * never claim a resource is absent on an `unknown` row - offering the idempotent
 * create action is still fine.
 */
export type VpcResourceStatus =
  | "present"
  | "missing"
  | "partial"
  | "unknown"
  | "skipped"
  | "error";

/** The system a resource lives in - drives the grouping in the flyout. */
export type VpcResourceSection =
  | "gitlab"
  | "vault"
  | "keycloak"
  | "docs"
  | "netbox"
  | "prtg"
  | "otobo"
  | "pangolin";

/**
 * How to create a missing resource: the endpoint to POST to plus the permission
 * that endpoint enforces, so the UI can hide an affordance the caller could not
 * use anyway. `permission` is an affordance hint ONLY - the route's own
 * `extractToken` remains the boundary.
 */
export interface VpcResourceAction {
  /** Stable id, also used as the i18n key suffix. */
  key: string;
  /** POST target relative to the API v2 root (no leading slash). */
  path: string;
  /** Fallback label when no translation exists. */
  label: string;
  permission: { resource: string; action: string };
  /**
   * True when the action runs the whole idempotent bootstrap rather than this
   * row alone - the UI says so, so nobody is surprised one button touched eight
   * resources.
   */
  runsFullBootstrap?: boolean;
}

export interface VpcResource {
  key: string;
  section: VpcResourceSection;
  label: string;
  status: VpcResourceStatus;
  /** Where it lives: GitLab path, Vault path, PRTG group, Pangolin site. */
  path?: string;
  /** State or failure detail. Never a secret value. */
  detail?: string;
  /** Deep link into the owning system. */
  href?: string;
  /** How to create it, when anketa can. */
  action?: VpcResourceAction;
  /** Set when only a human can fix it - shown instead of an action button. */
  hint?: string;
}

export interface VpcResourceInventory {
  vpc: string;
  owner: string;
  project: string;
  stage: string;
  /** CAPI-managed VPCs legitimately have no 200-k3-server / 210-k3s. */
  manageViaCapi: boolean;
  /** Questionnaire readiness issues - the bootstrap cannot run while non-empty. */
  readiness: string[];
  resources: VpcResource[];
  fetchedAt: string;
}

/** Outcome of one narrow create action. */
export interface VpcResourceActionResult {
  ok: boolean;
  action: string;
  message: string;
}
